// ======================================================================
// File Name       : EnterpriseAction
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import moment from 'moment';
import { AUTHORIZATION, DEFAULT_DATE_SHORT_FORMAT_SERVER, EVENT_NAME, MODE, OBJECT_ID, SELECT_TARGET_DATA, STRUCTURE_LEVEL } from '../constants';
import * as ActionTypes from '../constants/actionTypes/enterprise';
import { HIDE_MESSAGE } from '../constants/actionTypes/message';
import { PRICE_PLAN } from '../constants/EnterpriseConstants';
import { USE_DATE_RELEASE } from '../constants/EnterpriseNoticeConstants';
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_FROM, DEFAULT_DATE_TO } from '../constants/index';
import * as ModalIds from '../constants/ModalIds';
import * as utils from '../utils';
import * as enterpriseNoticeActionCreators from './actionCreators/enterpriseNotice';
import * as enterpriseRequests from './httpRequests/enterprise';
import * as modalActions from './ModalAction';


export function init() {
  return (dispatch, getState) => {
    const { authedUser } = getState().authed;
    const { enterpriseCd, enterpriseName } = authedUser;
    return dispatch(
      getStructure({
        selectTargetData: SELECT_TARGET_DATA.DATA,
        recursiveType: STRUCTURE_LEVEL.ALL,
      }),
    );
  };
}

export function exportCSV() {
  return dispatch =>
    dispatch(
      getStructure({
        selectTargetData: SELECT_TARGET_DATA.CSV,
        recursiveType: STRUCTURE_LEVEL.ALL,
      }),
    );
}

export function getStructure(param) {
  return dispatch =>
    enterpriseRequests.postEnterpriseGetStructure(param).then((apiRes) => {
      if (param.selectTargetData) {
        const url = apiRes && apiRes.csvfileUrl ? apiRes.csvfileUrl : null;
        utils.downloadFile(url);
        return null;
      } else {
        return dispatch({
          type: ActionTypes.ENTERPRISE_RECEIVE_GET_STRUCTURE,
          ...apiRes,
        });
      }
    });
}

export function openEnterpriseDetailDialog(enterpriseCd, pricePlan) {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.ENTERPRISE_INIT_EDIT,
      enterpriseCd,
      pricePlan,
    });
    dispatch(modalActions.openModal(ModalIds.ENTERPRISE_DETAIL));
  };
}

export function openEnterpriseRegisterDialog(parentInfoData) {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.ENTERPRISE_INIT_REGISTER,
      parentInfoData,
    });
    dispatch(modalActions.openModal(ModalIds.ENTERPRISE_REGISTER));
  };
}

export function updateEnterprise(payload) {
  return dispatch =>
    dispatch({
      type: ActionTypes.ENTERPRISE_UPDATE,
      payload,
    });
}

export function updateUsageSetingInfo(usageSettingInfo) {
  return dispatch =>
    dispatch({
      type: ActionTypes.ENTERPRISE_USAGE_INFO_UPDATE,
      usageSettingInfo,
    });
}

export function deleteEnterprise(enterpriseCd) {
  return async (dispatch) => {
    dispatch({ type: HIDE_MESSAGE });
    await enterpriseRequests.postEnterpriseEdit({
      editType: MODE.DELETE_MODE,
      enterpriseInfo: {
        enterpriseCd,
      },
    });

    dispatch(init());
  };
}

export function saveEnterprise() {
  return async (dispatch, getState) => {
    const { editingEnterprise, editMode } = getState().enterprise;
    const authed = getState().authed;
    const enterpriseInfo = { ...editingEnterprise };

    // STEP12.0.0 TAS-18710 START
    if (!authed.authedUser.rootFlag) {
      delete enterpriseInfo.manualPasswordSettingFlag;
    }
    // STEP12.0.0 TAS-18710 END

    delete enterpriseInfo.parentEnterpriseInfo;
    delete enterpriseInfo.usageSettingInfo.pricePlanUpdateReservedFlag;

    if (editMode === MODE.NEW_MODE) {
      if (editingEnterprise &&
        editingEnterprise.parentEnterpriseInfo &&
        editingEnterprise.parentEnterpriseInfo.parentEnterpriseCd) {
        enterpriseInfo.parentEnterpriseCd
          = editingEnterprise.parentEnterpriseInfo.parentEnterpriseCd;
      } else {
        enterpriseInfo.parentEnterpriseCd = authed.authedUser.enterpriseCd;
      }

      if (enterpriseInfo.usageSettingInfo.pricePlan === PRICE_PLAN.NEW) {
        const { unitPriceSettingInfo } = enterpriseInfo;
        const unitPriceSettingInfoRegist = {
          unitPriceSettingId: unitPriceSettingInfo.unitPriceSettingId,
          unitPriceSettingLiveUsageRate: unitPriceSettingInfo.unitPriceSettingLiveUsageRate,
        };
        enterpriseInfo.unitPriceSettingInfo = unitPriceSettingInfoRegist;
      }
    } else if (editMode === MODE.EDIT_MODE) { // sprint_20230403 #4 START
      const { enterpriseStartDate } = enterpriseInfo;
      enterpriseInfo.enterpriseStartDate = enterpriseStartDate ? moment(enterpriseStartDate).format(DEFAULT_DATE_SHORT_FORMAT_SERVER) : '';
    }
    // sprint_20230403 #4 END

    // Step 14.0.0 TAS-25448 START
    enterpriseInfo.unitPriceSettingInfo &&
      Object.keys(enterpriseInfo.unitPriceSettingInfo).forEach((key) => {
        if (enterpriseInfo.unitPriceSettingInfo[key] === '') {
          enterpriseInfo.unitPriceSettingInfo[key] = 0;
        }
      });
    // Step 14.0.0 TAS-25448 END
    await enterpriseRequests.postEnterpriseEdit({
      editType: editMode,
      enterpriseInfo,
    });
    dispatch(init());
  };
}

export function registerNoticeForEnterprise(notice) {
  return async (dispatch) => {
    if (notice.publicDateFrom) {
      notice.publicDateFrom = moment(notice.publicDateFrom).format(
        DEFAULT_DATE_SHORT_FORMAT_SERVER,
      );
    }
    if (notice.publicDateTo) {
      notice.publicDateTo = moment(notice.publicDateTo).format(DEFAULT_DATE_SHORT_FORMAT_SERVER);
    }
    try {
      await enterpriseRequests.postEnterpriseNoticeEdit({
        editType: MODE.NEW_MODE,
        noticeInfo: notice,
      });
      return null;
    } catch (error) {
      return error;
    }
  };
}

export function editNoticeForEnterprise(notice) {
  return async (dispatch) => {
    if (notice.publicDateFrom) {
      notice.publicDateFrom = moment(notice.publicDateFrom).format(
        DEFAULT_DATE_SHORT_FORMAT_SERVER,
      );
    } else {
      notice.publicDateFrom = '';
    }
    if (notice.publicDateTo) {
      notice.publicDateTo = moment(notice.publicDateTo).format(DEFAULT_DATE_SHORT_FORMAT_SERVER);
    } else {
      notice.publicDateTo = '';
    }
    try {
      await enterpriseRequests.postEnterpriseNoticeEdit({
        editType: MODE.EDIT_MODE,
        noticeInfo: notice,
      });
      return null;
    } catch (error) {
      return error;
    }
  };
}

export function deleteNoticeForEnterprise(notice) {
  return async (dispatch) => {
    await enterpriseRequests.postEnterpriseNoticeEdit({
      editType: MODE.DELETE_MODE,
      noticeInfo: notice,
    });
  };
}

export function getNoticeForEnterpriseDetail(searchInfo) {
  return async (dispatch) => {
    const response = await enterpriseRequests.postEnterpriseNoticeDetail(
      searchInfo,
    );
    const noticeListTemp = [];
    const noticeListForEdit = response.noticeList;
    if (noticeListForEdit && noticeListForEdit.length) {
      const notice = noticeListForEdit[0];
      const mDateF = moment(notice.publicDateFrom).startOf('day').format(DEFAULT_DATE_FORMAT);
      const mDateT = moment(notice.publicDateTo).startOf('day').format(DEFAULT_DATE_FORMAT);

      notice.releasePeriod = USE_DATE_RELEASE.RELEASE_DATE;

      if (mDateF == DEFAULT_DATE_FROM && mDateT == DEFAULT_DATE_TO) {
        notice.releasePeriod = USE_DATE_RELEASE.INDEFINITE_PERIOD;
      }
      if (mDateF == DEFAULT_DATE_FROM) {
        notice.publicDateFrom = null;
      }
      if (mDateT == DEFAULT_DATE_TO) {
        notice.publicDateTo = null;
      }
      noticeListTemp.push(notice);
    }
    dispatch(enterpriseNoticeActionCreators.getDetailOfNoticeForEnterprise(noticeListTemp));
  };
}

// sprint_20230403 #14 START
export function preCancelEnterprise(params) {
  return async (dispatch) => {
    try {
      await enterpriseRequests.postSignUpPreCancel(params);
      return null;
    } catch (error) {
      return error;
    }
  };
}
// sprint_20230403 #14 END

// sprint_20230418 #15 START
export function checkAvailabilityEnterprise(displayUserType) {
  return (dispatch) => {
    return enterpriseRequests.postCheckAvailability(displayUserType);
  };
}
// sprint_20230418 #15 END

// sprint_20230522_tma START
export function reasonForCancelEnterprise(data) {
  const { enterpriseCd, ssCancelReason1, ssCancelReason2 } = data;
  const params = {
    eventName: EVENT_NAME,
    properties: {
      ss_enterprise_cd: enterpriseCd,
      ss_cancel_reason_1: ssCancelReason1,
      ss_cancel_reason_2: ssCancelReason2,
    },
    objectId: OBJECT_ID,
  };
  return async (dispatch) => {
    try {
      await enterpriseRequests.reasonForCancelEnterprise(params);
      return null;
    } catch (error) {
      return error;
    }
  };
}
// sprint_20230522_tma END

export function saveEnterpriseReason(payload) {
  return dispatch =>
  dispatch({
    type: ActionTypes.ENTERPRISE_SAVE_REASON,
    payload,
  });
}